// WebSocket protocol between clients and the game server (one socket at /ws), plus the
// JSON shapes the HTTP API returns. Any breaking change must bump PROTOCOL_VERSION.

import type { GameResult, GameStatus } from '@chessbut/engine';

export const PROTOCOL_VERSION = 1;

export interface TimeControl {
  /** Seconds per side; 0 = untimed. */
  initial: number;
  /** Seconds added after each move. */
  increment: number;
}

/** Rated quick-match pools: one per variant, all on the same clock. */
export const QUICK_TIME_CONTROL: TimeControl = { initial: 300, increment: 3 };

export type ColorChoice = 'white' | 'black' | 'random';
export type GameMode = 'quick' | 'private';

export interface PlayerInfo {
  id: string;
  name: string;
  /** Account username for profile links; null for guests. */
  username: string | null;
  /** Rating in this game's variant (rounded), null in unrated games. */
  rating: number | null;
  provisional: boolean;
  /** The computer's level (1-5) when this seat is a rated bot; absent for people. */
  bot?: number;
}

export interface ClockState {
  white: number;
  black: number;
  /** 0 white, 1 black, null when stopped. */
  running: 0 | 1 | null;
}

export interface RatingChange {
  before: number;
  after: number;
  diff: number;
}

export interface GameSnapshot {
  id: string;
  variant: string;
  mode: GameMode;
  rated: boolean;
  white: PlayerInfo;
  black: PlayerInfo;
  /** Your colour: 0 white, 1 black, null when spectating. */
  you: 0 | 1 | null;
  startFen: string;
  moves: string[];
  timeControl: TimeControl;
  clock: ClockState | null;
  status: GameStatus;
  drawOffer: 0 | 1 | null;
  rematchOffer: 0 | 1 | null;
  /** Connected state of [white, black]. */
  connected: [boolean, boolean];
  left: [boolean, boolean];
  ratings: { white: RatingChange; black: RatingChange } | null;
  points: { white: number; black: number } | null;
  startedAt: number;
}

/** A computer opponent and its rating in one variant. */
export interface BotOffer {
  level: number;
  rating: number;
}

export interface LiveStats {
  online: number;
  playing: number;
  /** Players searching, per variant. */
  searching: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Client → server

export type ClientMessage =
  | { t: 'hello'; token: string; client: string; version: number }
  | { t: 'ping' }
  | { t: 'queue.join'; variant: string }
  | { t: 'queue.leave' }
  /** Nobody else is searching: play the offered rated computer instead. */
  | { t: 'queue.bot' }
  | { t: 'room.create'; variant: string; timeControl: TimeControl; color: ColorChoice }
  | { t: 'room.cancel' }
  | { t: 'room.join'; code: string }
  | { t: 'move'; gameId: string; uci: string; ply: number }
  | { t: 'resign'; gameId: string }
  | { t: 'abort'; gameId: string }
  | { t: 'draw'; gameId: string; action: 'offer' | 'accept' | 'decline' }
  | { t: 'rematch'; gameId: string; action: 'offer' | 'accept' | 'decline' }
  | { t: 'leave'; gameId: string }
  | { t: 'sync'; gameId: string }
  | { t: 'watch'; gameId: string }
  | { t: 'unwatch'; gameId: string };

// ---------------------------------------------------------------------------
// Server → client

export type ErrorCode =
  | 'bad-message'
  | 'rate-limited'
  | 'not-ready'
  | 'unauthorized'
  | 'outdated'
  | 'replaced'
  | 'unknown-variant'
  | 'not-released'
  | 'room-not-found'
  | 'own-room'
  | 'in-game'
  | 'no-game'
  | 'illegal-move'
  | 'not-your-turn'
  | 'banned';

export type ServerMessage =
  | { t: 'welcome'; playerId: string; stats: LiveStats }
  | { t: 'stats'; stats: LiveStats }
  | {
      t: 'queue';
      variant: string | null;
      since: number | null;
      /** The rated computer offered if nobody else turns up (from `since + RATED_BOT_AFTER_MS`). */
      bot?: BotOffer | null;
    }
  | { t: 'room'; code: string | null; variant: string | null; timeControl: TimeControl | null; color: ColorChoice | null }
  | { t: 'game'; game: GameSnapshot }
  | { t: 'move'; gameId: string; uci: string; san: string; ply: number; clock: ClockState | null }
  | {
      t: 'end';
      gameId: string;
      status: GameStatus;
      clock: ClockState | null;
      ratings: GameSnapshot['ratings'];
      points: GameSnapshot['points'];
    }
  | { t: 'offers'; gameId: string; drawOffer: 0 | 1 | null; rematchOffer: 0 | 1 | null }
  | { t: 'presence'; gameId: string; connected: [boolean, boolean]; left: [boolean, boolean]; graceSeconds: number | null }
  | { t: 'error'; code: ErrorCode; message: string }
  | { t: 'pong' };

export const MAX_MESSAGE_BYTES = 4096;

// ---------------------------------------------------------------------------
// HTTP API shapes

export interface RatingSummary {
  pool: string;
  rating: number;
  rd: number;
  provisional: boolean;
  games: number;
  wins: number;
  losses: number;
  draws: number;
  best: number;
}

export interface Me {
  id: string;
  name: string;
  username: string | null;
  guest: boolean;
  admin: boolean;
  plus: boolean;
  createdAt: number;
  ratings: Record<string, RatingSummary>;
  today: { points: number; games: number; rank: number | null };
  streak: { current: number; best: number; playedToday: boolean };
  challenge: { done: boolean; result: string | null; plies: number | null };
  /** The email this account signs in with (only ever sent to its owner). */
  email: string | null;
  hasPassword: boolean;
  /** Linked sign-in providers ('google', 'apple'). */
  providers: string[];
}

/** Ways to sign in this server offers. */
export interface AuthOptions {
  email: boolean;
  password: boolean;
  google: boolean;
  apple: boolean;
}

export interface LeaderboardRow {
  rank: number;
  playerId: string;
  name: string;
  username: string | null;
  value: number;
  games: number;
  provisional?: boolean;
}

export interface Leaderboard {
  kind: 'daily' | 'rating' | 'challenge';
  key: string;
  rows: LeaderboardRow[];
  me: LeaderboardRow | null;
  total: number;
}

export interface GameRecordSummary {
  id: string;
  variant: string;
  mode: string;
  rated: boolean;
  white: { id: string | null; name: string; username: string | null; rating: number | null; diff: number | null };
  black: { id: string | null; name: string; username: string | null; rating: number | null; diff: number | null };
  botLevel: number | null;
  result: GameResult | null;
  reason: string | null;
  plies: number;
  startedAt: number;
  endedAt: number | null;
}
